import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { readCookieConsent, setCookieConsent } from '@/lib/cookie-consent';

/**
 * Site-wide notice until the visitor chooses essential-only or all cookies.
 * Essential auth cookies are always set by the API; this only records optional consent.
 */
export const CookieBanner = () => {
  const { t } = useTranslation('legal');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!readCookieConsent()) setVisible(true);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    setCookieConsent({ analytics: true });
    setVisible(false);
  };

  const essentialOnly = () => {
    setCookieConsent({ analytics: false });
    setVisible(false);
  };

  return (
    <section
      aria-label={t('banner.ariaLabel')}
      aria-live="polite"
      className="fixed inset-x-3 bottom-3 z-[1400] mx-auto max-w-xl rounded-xl border border-border bg-surface p-4 shadow-e4 sm:inset-x-4 sm:bottom-4 sm:p-5"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-card-title font-semibold text-fg">{t('banner.title')}</p>
          <p className="text-body-sm leading-relaxed text-fg-secondary">
            {t('banner.body')}{' '}
            <Link to="/cookies" className="font-medium text-brand underline-offset-4 hover:underline">
              {t('banner.learnMore')}
            </Link>
          </p>
        </div>
        <div className="flex shrink-0 justify-end gap-2">
          <Button type="button" size="sm" variant="outline" onClick={essentialOnly}>
            {t('banner.essential')}
          </Button>
          <Button type="button" size="sm" onClick={acceptAll}>
            {t('banner.accept')}
          </Button>
        </div>
      </div>
    </section>
  );
};
